import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "394",
  "slug": "what-is-a-great-circle-route",
  "title": "What Is a Great Circle Route? Why Planes Fly Over the Arctic",
  "excerpt": "The shortest path between two cities often looks like a strange curve on a flat map. Great circle routes explain why your flight to Asia heads up toward the pole.",
  "date": "2026-06-10",
  "category": "Geography",
  "image": "https://images.unsplash.com/photo-1606768666853-403c90a981ad?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxhaXJwbGFuZSUyMHNreSUyMGZsaWdodHxlbnwxfDB8fHwxNzgxMDc5MjE3fDA&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "3 min",
  "author": "EarthGuessr Team",
  "tldr": "A great circle is the shortest path between two points on a sphere. On a flat Mercator map it looks like an arc bending toward the pole, which is why long-haul flights curve north instead of flying in a 'straight' line.",
  "content": [
    {
      "type": "paragraph",
      "text": "Pull up a flight tracker for a trip from New York to Tokyo and something looks wrong. Instead of heading west in a tidy straight line, the plane arcs north, skimming past Alaska and the edge of the Arctic before dropping down into Japan. The pilot is not lost. The plane is following a great circle, the genuinely shortest path across a round planet, even though it looks like a detour on a flat map."
    },
    {
      "type": "heading",
      "text": "Straight Lines Lie on Flat Maps"
    },
    {
      "type": "paragraph",
      "text": "A great circle is any circle drawn on a sphere whose centre is the centre of the sphere itself. The equator is a great circle, and so is every line of longitude. Crucially, the shortest distance between any two points on a globe always lies along the great circle that connects them. On a globe this is obvious: stretch a string between two cities and it naturally hugs the surface along the shortest route."
    },
    {
      "type": "heading",
      "text": "Why the Shortest Route Curves"
    },
    {
      "type": "paragraph",
      "text": "The trouble starts when you flatten the globe onto a map. The familiar Mercator projection stretches the high latitudes enormously, which is why Greenland looks as big as Africa. That same stretching takes a great circle, which in reality bows toward the nearest pole, and draws it as a curved arc. A path from London to Los Angeles really does pass near Greenland; a path from New York to Tokyo really does run close to the Arctic. The curve on the screen is the straight, efficient route in disguise."
    },
    {
      "type": "heading",
      "text": "The Rhumb Line Trade-Off"
    },
    {
      "type": "paragraph",
      "text": "There is another kind of route, called a rhumb line, that crosses every meridian at the same angle. On a Mercator map a rhumb line is a perfectly straight line, which is exactly why the projection was invented: a sailor could draw a straight pencil line and hold a single constant compass bearing all the way. The catch is that a rhumb line is longer than a great circle. For centuries, navigators happily traded a few extra miles for the simplicity of not constantly re-checking their heading."
    },
    {
      "type": "list",
      "items": [
        "New York to Tokyo: arcs north past Alaska and the Aleutians",
        "London to Los Angeles: curves up over Greenland and northern Canada",
        "Sydney to Santiago: dips far south, skirting Antarctic waters",
        "Each looks like a detour on a flat map, yet each is the shortest real path"
      ]
    },
    {
      "type": "heading",
      "text": "See It for Yourself"
    },
    {
      "type": "paragraph",
      "text": "The cleanest demonstration is a globe and a piece of string. Pin one end on your home city, pull the string taut to a distant city, and watch the route bend toward the pole. Online great-circle mapping tools do the same thing digitally, drawing the true shortest path and then showing how badly a flat map distorts it."
    }
  ]
};
